import React from 'react';
import { ApiProvider, MockedApiProvider } from 'Services/api';
import { AuthServiceProvider } from 'App/components/utils/providers/AuthProvider';
import { ProductServiceProvider } from 'App/components/utils/providers/ProductProvider';
import { CustomerServiceProvider } from 'App/components/utils/providers/CustomerProvider';
import { LeadServiceProvider } from 'App/components/utils/providers/LeadProvider';
import { LeadVersionServiceProvider } from 'App/components/utils/providers/LeadVersionServiceProvider';
import { LeadStatusServiceProvider } from 'App/components/utils/providers/LeadStatusServiceProvider';
import { UserApiServiceProvider } from 'App/components/utils/providers/UserApiProvider';
import { DashboardServiceProvider } from 'App/components/utils/providers/DashboardProvider';
import { PolicyServiceProvider } from 'App/components/utils/providers/PolicyProvider';
import { PaymentServiceProvider } from 'App/components/utils/providers/PaymentProvider';
import { AccountingServiceProvider } from 'App/components/utils/providers/AccountingProvider';
import { DocumentServiceProvider } from 'App/components/utils/providers/DocumentProvider';
import { ProductDocumentProvider } from 'App/components/utils/providers/ProductDocumentProvider';
import { DocxTemplateServiceProvider } from 'App/components/utils/providers/DocxTemplateProvider';
import { EmailServiceProvider } from 'App/components/utils/providers/EmailProvider';
import { BillingServiceProvider } from 'App/components/utils/providers/BillingProvider';
import { GdvServiceProvider } from 'App/components/utils/providers/GdvProvider';
import { AlertServiceProvider } from 'App/components/utils/providers/AlertProvider';
import { HelpCenterServiceProvider } from 'App/components/utils/providers/HelpCenterProvider';
import { ReportServiceProvider } from 'App/components/utils/providers/ReportProvider';
import { TariffComparisonServiceProvider } from 'App/components/utils/providers/TariffComparisonProvider';
import { WFMonitorServiceProvider } from 'App/components/utils/providers/WFMonitorProvider';
import { ClaimServiceProvider } from 'App/components/utils/providers/ClaimProvider';
import { ClaimStatusServiceProvider } from 'App/components/utils/providers/ClaimStatusServiceProvider';
import { PartnerTypeServiceProvider } from 'App/components/utils/providers/PartnerTypeProvider';
import { PartnerServiceProvider } from 'App/components/utils/providers/PartnerProvider';
import { StatusTransitionRuleServiceProvider } from 'App/components/utils/providers/StatusTransitionProvider';
import { WorkFlowServiceProvider } from 'App/components/utils/providers/WorkFlowProvider';
import { SettingsProvider } from 'App/components/utils/providers/SettingsProvider';
import { SettingsServiceApiProvider } from 'App/components/utils/providers/SettingsServiceApiProvider';
import { PublicApiIdentifierProvider } from 'App/components/utils/providers/PublicApiIdentifierProvider';
import { PluginServiceProvider } from 'App/components/utils/providers/PluginProvider';
import { UsersServiceProvider, UserServiceProvider } from 'App/components/utils/providers/UserProvider';
import { RoleServiceProvider } from 'App/components/utils/providers/RoleProvider';
import { FormServiceProvider } from 'App/components/utils/providers/FormProvider';
import { AuthService } from 'Services/auth';
import { ProductService } from 'Services/product';
import { CustomerService } from 'Services/customer';
import { LeadService } from 'Services/lead';
import { LeadVersionService } from 'Services/lead-version';
import { LeadStatusService } from 'Services/lead-status';
import { UserApiService } from 'Services/user';
import { DashboardService } from 'Services/dashboard';
import { PolicyService } from 'Services/policies/new/service';
import { PaymentsService } from 'Services/payments';
import { AccountingService } from 'Services/accounting';
import { DocumentService } from 'Services/document';
import { ProductDocumentService } from 'Services/product-document';
import { DocxTemplateService } from 'Services/docx-template';
import { EmailService } from 'Services/email';
import { BillingService } from 'Services/billing';
import { GdvService } from 'Services/gdv';
import { AlertService } from 'Services/alert';
import { HelpCenterService } from 'Services/helpCenter';
import { ReportService } from 'Services/report';
import { TariffComparisonService } from 'Services/tariff-comparison';
import { WFMonitorService } from 'Services/workflow-monitor';
import { ClaimService } from 'Services/claim';
import { ClaimStatusService } from 'Services/claim-status';
import { PartnerTypeService } from 'Services/partner-type';
import { PartnerService } from 'Services/partner';
import { StatusTransitionRuleService } from 'Services/status-transition-rule';
import { SettingsService } from 'Services/settings';
import { SettingsServiceApi } from 'Services/settings-service/settings-service';
import { MockedWorkFlowService } from 'Services/workflow/mock-service';
import { MockedSettingsService } from 'Services/api/settings/mock-service';
import { MockedPluginService } from 'Services/plugin/mock-service';
import { MockedUsersService, MockedUserService } from 'Services/api/users/mock-service';
import { MockedRoleService } from 'Services/api/role/mock-service';
import { FormService } from 'Services/forms/base';
import { composeProviders } from './composeProviders';
import { CommissionServiceProvider } from 'App/components/utils/providers/CommissionProvider';
import { CommissionService } from 'Services/commission';

interface ServiceProvidersProps {
  children: React.ReactNode;
}

/**
 * Core Services - Authentication, Products, Customers
 */
const coreServiceProviders = [
  { Provider: AuthServiceProvider, props: { useClass: AuthService } },
  { Provider: ProductServiceProvider, props: { useClass: ProductService } },
  { Provider: CustomerServiceProvider, props: { useClass: CustomerService } },
  { Provider: SettingsProvider, props: { useClass: SettingsService } },
  { Provider: SettingsServiceApiProvider, props: { useClass: SettingsServiceApi } },
];

/**
 * Lead Management Services
 */
const leadServiceProviders = [
  { Provider: LeadServiceProvider, props: { useClass: LeadService } },
  { Provider: LeadVersionServiceProvider, props: { useClass: LeadVersionService } },
  { Provider: LeadStatusServiceProvider, props: { useClass: LeadStatusService } },
];

/**
 * User & Dashboard Services
 */
const userDashboardProviders = [
  { Provider: UserApiServiceProvider, props: { useClass: UserApiService } },
  { Provider: DashboardServiceProvider, props: { useClass: DashboardService } },
];

/**
 * Policy & Financial Services
 */
const policyFinanceProviders = [
  { Provider: PolicyServiceProvider, props: { useClass: PolicyService } },
  { Provider: PaymentServiceProvider, props: { useClass: PaymentsService } },
  { Provider: AccountingServiceProvider, props: { useClass: AccountingService } },
  { Provider: BillingServiceProvider, props: { useClass: BillingService } },
  { Provider: CommissionServiceProvider, props: { useClass: CommissionService } },
];

/**
 * Document Services
 */
const documentProviders = [
  { Provider: DocumentServiceProvider, props: { useClass: DocumentService } },
  { Provider: ProductDocumentProvider, props: { useClass: ProductDocumentService } },
  { Provider: DocxTemplateServiceProvider, props: { useClass: DocxTemplateService } },
];

/**
 * Communication & Integration Services
 */
const communicationProviders = [
  { Provider: EmailServiceProvider, props: { useClass: EmailService } },
  { Provider: GdvServiceProvider, props: { useClass: GdvService } },
  { Provider: AlertServiceProvider, props: { useClass: AlertService } },
  { Provider: HelpCenterServiceProvider, props: { useClass: HelpCenterService } },
];

/**
 * Reporting & Monitoring Services
 */
const reportingProviders = [
  { Provider: ReportServiceProvider, props: { useClass: ReportService } },
  { Provider: TariffComparisonServiceProvider, props: { useClass: TariffComparisonService } },
  { Provider: WFMonitorServiceProvider, props: { useClass: WFMonitorService } },
];

/**
 * Claims & Partners Services
 */
const claimsPartnersProviders = [
  { Provider: ClaimServiceProvider, props: { useClass: ClaimService } },
  { Provider: ClaimStatusServiceProvider, props: { useClass: ClaimStatusService } },
  { Provider: PartnerTypeServiceProvider, props: { useClass: PartnerTypeService } },
  { Provider: PartnerServiceProvider, props: { useClass: PartnerService } },
  { Provider: StatusTransitionRuleServiceProvider, props: { useClass: StatusTransitionRuleService } },
];

/**
 * Mocked Services
 */
const mockedServiceProviders = [
  { Provider: WorkFlowServiceProvider, props: { useClass: MockedWorkFlowService } },
  { Provider: SettingsProvider, props: { useClass: MockedSettingsService } },
  { Provider: PluginServiceProvider, props: { useClass: MockedPluginService } },
  { Provider: UsersServiceProvider, props: { useClass: MockedUsersService } },
  { Provider: UserServiceProvider, props: { useClass: MockedUserService } },
  { Provider: RoleServiceProvider, props: { useClass: MockedRoleService } },
  { Provider: FormServiceProvider, props: { useClass: FormService } },
];

/**
 * Compose all Real API service providers
 */
const allRealServiceProviders = [
  ...coreServiceProviders,
  ...leadServiceProviders,
  ...userDashboardProviders,
  ...policyFinanceProviders,
  ...documentProviders,
  ...communicationProviders,
  ...reportingProviders,
  ...claimsPartnersProviders,
  { Provider: PublicApiIdentifierProvider, props: {} },
];

const ComposedRealServices = composeProviders( allRealServiceProviders );
const ComposedMockedServices = composeProviders( mockedServiceProviders );

/**
 * Real API Service Providers
 * These providers connect to actual backend services
 */
export const RealServiceProviders: React.FC<ServiceProvidersProps> = ( { children } ) => (
  <ApiProvider>
    <ComposedRealServices>
      { children }
    </ComposedRealServices>
  </ApiProvider>
);

/**
 * Mocked Service Providers
 * These providers use mocked data for development/testing
 */
export const MockedServiceProviders: React.FC<ServiceProvidersProps> = ( { children } ) => (
  <MockedApiProvider>
    <ComposedMockedServices>
      { children }
    </ComposedMockedServices>
  </MockedApiProvider>
);
